import { useMemo } from 'react';
import { Layout } from '@components/Layout';
import { useAuth } from '@context/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import { Card, DataTable, EmptyState } from '@/components/UI';
import { trpcUnbatched } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { capitalizeFirstLetter, calculatePercentageChangeFast } from '@einhaus/meteoric-helpers';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { buildAgreementRedirectPath } from '@/utils/agreements';
import MarketOverviewCard from '@/components/Market/MarketOverviewCard';

interface PortfolioBalanceSummary {
    currentEquity: number;
    lastClosingEquity: number;
    todayChangePercent: number;
}

interface DashboardPortfolioRow {
    portfolioId: number;
    portfolioName: string;
    tradingAccountName: string;
    portfolioType: string;
    activeStrategiesCount: number;
    positionsCount: number;
    cashBalance: number;
    positionsValue: number;
    lastClosingValue: number;
    balanceSummary?: Partial<PortfolioBalanceSummary> | null;
}

const getPortfolioEquity = (portfolio: DashboardPortfolioRow) => {
    return portfolio.balanceSummary?.currentEquity ?? portfolio.cashBalance + portfolio.positionsValue;
};

const getPortfolioLastClosingEquity = (portfolio: DashboardPortfolioRow) => {
    return portfolio.balanceSummary?.lastClosingEquity ?? portfolio.lastClosingValue;
};

const getPortfolioTodayChangePercent = (portfolio: DashboardPortfolioRow) => {
    return (
        portfolio.balanceSummary?.todayChangePercent ??
        calculatePercentageChangeFast(getPortfolioLastClosingEquity(portfolio), getPortfolioEquity(portfolio))
    );
};

const Home = () => {
    const { isAuthenticated, user, isLoading, requiresTwoFactorSetup, hasPendingRequiredAgreements, pendingRequiredAgreements } = useAuth();

    // Keep each card independent, including transport: a non-streaming batch waits for its slowest query.
    const checklistQuery = useAppQuery(
        {
            ...trpcUnbatched.user.getHomeChecklist.queryOptions(),
            enabled: isAuthenticated && !isLoading && !requiresTwoFactorSetup,
            staleTime: 30000
        },
        { fallbackErrorMessage: 'Failed to load checklist work', showErrorToast: true }
    );
    const portfoliosQuery = useAppQuery(
        {
            ...trpcUnbatched.user.getHomePortfolios.queryOptions(),
            enabled: isAuthenticated && !isLoading && !requiresTwoFactorSetup,
            staleTime: 30000
        },
        { fallbackErrorMessage: 'Failed to load home portfolios', showErrorToast: true }
    );

    type HomePortfolioRow = NonNullable<typeof portfoliosQuery.data>['items'][number] & DashboardPortfolioRow;
    const portfolioRows = useMemo<HomePortfolioRow[]>(() => portfoliosQuery.data?.items ?? [], [portfoliosQuery.data]);

    const portfolioColumns: ColumnDef<HomePortfolioRow>[] = useMemo(
        () => [
            {
                accessorKey: 'portfolioName',
                header: 'Portfolio',
                cell: (info) => {
                    const p = info.row.original;
                    return (
                        <>
                            <Link className="text-blue-400 hover:text-blue-300 font-medium" to={`/user/portfolio/${p.portfolioId}`}>
                                {p.portfolioName}
                            </Link>
                            <div className="text-xs text-zinc-500 mt-0.5">
                                {p.tradingAccountName} • {capitalizeFirstLetter(p.portfolioType)}
                            </div>
                        </>
                    );
                },
                size: 320
            },
            {
                accessorKey: 'activeStrategiesCount',
                header: 'Running Strategies',
                cell: (info) => `${info.getValue<number>()}`,
                size: 90
            },
            {
                accessorKey: 'positionsCount',
                header: 'Positions',
                cell: (info) => `${info.getValue<number>()}`,
                size: 80
            },
            {
                id: 'balance',
                header: 'Current Equity',
                accessorFn: (p) => getPortfolioEquity(p),
                cell: (info) => `$${info.getValue<number>().toFixed(2)}`,
                size: 120
            },
            {
                id: 'pctChangeToday',
                header: '% Today',
                accessorFn: (p) => getPortfolioTodayChangePercent(p),
                cell: (info) => {
                    const pct = info.getValue<number>();
                    const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';
                    const cls = dir === 'up' ? 'text-profit-green' : dir === 'down' ? 'text-red-500' : 'text-zinc-400';
                    return <span className={cls}>{pct.toFixed(2)}%</span>;
                },
                size: 80
            }
        ],
        []
    );

    // Content to render after authentication state is determined
    const renderContent = () => {
        if (isLoading) {
            // Return empty div while loading - the spinner will be shown by Layout
            return <div />;
        }

        if (!isAuthenticated) {
            return (
                <div className="bg-zinc-800 rounded-lg shadow-lg p-6 my-5">
                    <h2 className="text-2xl font-bold text-white">Welcome to Project 314</h2>
                    <p className="mt-3 text-zinc-300">
                        Please{' '}
                        <Link className="text-primary hover:text-blue-400 underline" to="/login">
                            log in
                        </Link>{' '}
                        or{' '}
                        <Link className="text-primary hover:text-blue-400 underline" to="/register">
                            register
                        </Link>{' '}
                        to access all features.
                    </p>
                </div>
            );
        }

        return (
            <div className="my-5 space-y-6">
                <div className="bg-zinc-800 rounded-lg shadow-lg p-6">
                    <h2 className="text-2xl font-bold text-white">Welcome back</h2>
                    {user && <p className="mt-3 text-zinc-300">You are logged in as {user.email}</p>}
                </div>

                <div className="space-y-6">
                    <MarketOverviewCard />
                    <Card subtitle="Strategies with incomplete checklist items" title="Checklist Work">
                        {!checklistQuery.data ? (
                            <div className="text-zinc-400 text-sm">
                                {checklistQuery.isError ? (
                                    <span role="alert">
                                        Could not load checklist work.{' '}
                                        <button className="text-blue-400" onClick={() => void checklistQuery.refetch()}>
                                            Retry
                                        </button>
                                    </span>
                                ) : (
                                    'Loading…'
                                )}
                            </div>
                        ) : checklistQuery.data.items.length === 0 ? (
                            <EmptyState size="sm" title="No incomplete checklist items found." />
                        ) : (
                            <div className="space-y-3">
                                {checklistQuery.data.items.map((s) => (
                                    <div className="flex items-start justify-between gap-3" key={s.strategyId}>
                                        <div className="min-w-0">
                                            <Link
                                                className="text-blue-400 hover:text-blue-300 font-medium"
                                                to={`/user/strategyDashboard/${s.strategyId}`}
                                            >
                                                {s.strategyName}
                                            </Link>
                                            <div className="text-xs text-zinc-500 mt-0.5">
                                                {s.strategyActive ? 'Active' : 'Inactive'}
                                                {s.topIncompleteItems.length > 0
                                                    ? ` • ${s.topIncompleteItems.map((i) => i.name).join(', ')}`
                                                    : ''}
                                            </div>
                                        </div>
                                        <span className="shrink-0 rounded bg-amber-900/40 text-amber-200 border border-amber-700 px-2 py-1 text-xs font-medium">
                                            {s.incompleteCount} open
                                        </span>
                                    </div>
                                ))}

                                {checklistQuery.data.totalCount > checklistQuery.data.items.length && (
                                    <div className="pt-2 border-t border-zinc-800">
                                        <Link className="text-sm text-blue-400 hover:text-blue-300" to="/user/tradingStrategies">
                                            View all strategies
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>

                    <Card subtitle="Portfolios with running strategies on trading accounts" title="Portfolios on Trading Accounts">
                        {!portfoliosQuery.data ? (
                            <div className="text-zinc-400 text-sm">
                                {portfoliosQuery.isError ? (
                                    <span role="alert">
                                        Could not load portfolios.{' '}
                                        <button className="text-blue-400" onClick={() => void portfoliosQuery.refetch()}>
                                            Retry
                                        </button>
                                    </span>
                                ) : (
                                    'Loading…'
                                )}
                            </div>
                        ) : portfoliosQuery.data.items.length === 0 ? (
                            <EmptyState size="sm" title="No portfolios with running strategies on trading accounts found." />
                        ) : (
                            <div className="space-y-3">
                                <DataTable
                                    sort
                                    striped
                                    columns={portfolioColumns}
                                    data={portfolioRows}
                                    pagination={{ enabled: false, pageSize: 10 }}
                                    search={false}
                                />

                                {portfoliosQuery.data.totalCount > portfoliosQuery.data.items.length && (
                                    <div className="pt-2 border-t border-zinc-800">
                                        <Link className="text-sm text-blue-400 hover:text-blue-300" to="/user/portfolios">
                                            View all portfolios
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        );
    };

    if (!isLoading && isAuthenticated && hasPendingRequiredAgreements) {
        return <Navigate replace to={buildAgreementRedirectPath({ pendingRequiredAgreements, redirectTarget: '/' })} />;
    }

    if (!isLoading && isAuthenticated && requiresTwoFactorSetup) {
        return <Navigate replace to="/user/enable2fa?redirect=%2Fuser%2Fportfolios" />;
    }

    return <Layout title="Project 314">{renderContent()}</Layout>;
};

export default Home;
